import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, Form, FormikValues } from 'formik';
import * as yup from 'yup';

import Field from 'components/FieldWithoutGrid';
import useStoreTranslation from 'hooks/useStoreTranslation';

import {
  Container,
  Header,
  Subheader,
  FieldsContainer,
  Input,
  ResendButton,
  BackButton,
  NextButton,
  ButtonsContainer,
  ButtonLine,
} from '../../styles';
import { TelephoneValues } from '../Telephone';
import { AuthenticationCard, Email, Detail } from './styles';

export interface TwoFactorData {
  code: string;
  message: string;
  errorDescription: string;
  key: string;
  verification_type: string;
  verification_value: string;
  retry_by_email: boolean;
  flow_type: string;
}

export interface TwoFactorCodeValues {
  code: string;
}

interface Props {
  values: TwoFactorCodeValues | null;
  onSubmit: (values: TwoFactorCodeValues) => Promise<any>;
  onResend: () => Promise<any> | undefined;
  data: TwoFactorData | null;
  telephone: TelephoneValues | null;
}

const TwoFactor = ({ values, onSubmit, onResend, data, telephone }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const formRef = useRef<any>();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  if (!telephone || !data) {
    if (history.location.pathname === '/login/two-factor') history.replace('/login');
    return null;
  }

  const validationSchema = yup.object().shape({
    code: yup.string().length(6).required(),
  });

  const initialValues: TwoFactorCodeValues = {
    code: '',
  };

  const handleBack = () => history.goBack();

  const handleSubmit = async (formValues: TwoFactorCodeValues) => {
    setLoading(true);
    await onSubmit(formValues);
    setLoading(false);
  };

  const handleResend = async () => {
    setResendLoading(true);
    await onResend();
    if (formRef.current) {
      formRef.current.setFieldValue('code', '');
    }
    setResendLoading(false);
  };

  return (
    <Formik
      innerRef={formRef}
      initialStatus={{ rightCheckIfIsValid: true }}
      initialValues={values || initialValues}
      isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid }: FormikValues) => (
        <Form>
          <Container center>
            <AuthenticationCard alt="Card icon" src="/corporate/images/authentication-card.png" />
            <Header>{t('login.twoFactor.title')}</Header>
            <Subheader>{t('login.twoFactor.subtitle')}</Subheader>
            <Email>{data.verification_value}</Email>

            <Detail>{t('login.twoFactor.detail')}</Detail>
            <FieldsContainer>
              <Field name="code">
                <Input
                  placeholder={t('login.twoFactor.placeholder')}
                  autoCapitalize="off"
                  autoCorrect="off"
                  maxLength={6}
                  type="tel"
                />
              </Field>
            </FieldsContainer>

            <ResendButton type="button" onClick={handleResend} disabled={resendLoading} loading={resendLoading}>
              {t('login.twoFactor.resendCode')}
            </ResendButton>

            <ButtonsContainer>
              <ButtonLine>
                <BackButton type="button" onClick={handleBack}>
                  {t('login.backButton')}
                </BackButton>
                <NextButton disabled={!isValid || loading} loading={loading}>
                  {t('login.twoFactor.verifyButton')}
                </NextButton>
              </ButtonLine>
            </ButtonsContainer>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default TwoFactor;
